// Import package components
import { useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Image,
  Navbar,
  NavbarContainer,
  NavbarDropdown,
  NavItem,
} from "uikit-react";
import UIkit from "uikit";
import moment from "moment";
import { Link as RawLink } from "../ui-kit/Link";
import { AppContexts } from "../../providers";
import feather from "feather-icons";

import Notification from "./notification";
import TrialAccount from "../common/TrialAccount";

// Helper component to render Feather icons
const FeatherIcon = ({ name, className = "" }) => {
  const icon = feather.icons[name];
  if (!icon) return null;
  
  return (
    <svg
      className={`feather-icon ${className}`}
      viewBox="0 0 24 24"
      dangerouslySetInnerHTML={{ __html: icon.contents }}
      style={{ color: "var(--dark-purple)" }}
    />
  );
};

const Link = ({ onClick, ...props }) => (
  <RawLink
    onClick={() => {
      onClick && onClick();
    }}
    {...props}
  />
);

const Header = () => {
  const { user: account, inbox, logout } = useContext(AppContexts.AuthContext);
  const { newOrderCount, getNewOrderCount } = useContext(
    AppContexts.OrdersContext,
  );

  const location = useLocation();
  const hasActiveTrial =
    ["trial", "trial-full"].includes(account?.details?.type) &&
    moment(account?.details?.trial_end).isAfter(moment());

  //const history = useHistory();

  const handleLogout = async () => {
    console.log("logging out");

    logout()
      .then(() => {
        console.log("Goodbye");
      })
      .catch((err) => {
        console.warn("Error logging out:", err);
      });
  };

  const profileImage = useMemo(() => {
    return (
      (account?.user?.attachments || []).find((a) => a.type === "image") || null
    );
  }, [account]);

  useEffect(() => {
    getNewOrderCount();
  }, []);

  const isActive = (paths) => {
    if (!Array.isArray(paths)) {
      paths = [paths];
    }
    return paths.some((path) => {
      const regex = new RegExp(`^${path}`);
      return regex.test(location.pathname);
    })
      ? "uk-active"
      : "";
  };

  const hasExtras = () => {
    return (account?.details?.product || "").split(",").includes("extras");
  };

  const hasFlag = (flagName) => {
    const accountFlags = account?.flags || [];
    return accountFlags.some((f) => f.name === flagName);
  };

  // const isOnboardingUser = () => {
  //   return account && account?.details?.type === 'onboarding';
  // }

  return (
    <>
      <div
        id="ruebarue-navbar"
        className={`rbr-header-sticky${
          hasActiveTrial ? " has-trial-account" : ""
        }`}
      >
        <NavbarContainer className="hms-navbar">
          <div className="rbr-header-content">
            <TrialAccount />
            <div
              className="navbar-collapse"
              id="navbar_collapse"
            >
              {!!account && (
                <Navbar right>
                  {account?.details?.type === "onboarding" && (
                    <NavItem className={isActive(["/getting-started"])}>
                      <Link
                        to="/getting-started"
                        uk-tooltip="Get Started"
                        rel="noopener noreferrer"
                      >
                        <Image
                          src="/images/getting-started.svg"
                          className="margin-zero"
                        />
                      </Link>
                    </NavItem>
                  )}
                  <NavItem className={`nav-admin-item ${isActive([
                  "/team",
                  "/zones",
                  "/settings",
                  "/billing",
                  "/payments",
                  "/units",
                  "/macros",
                  ...(hasFlag("crm") ? ["/custom-fields"] : []),
                  "/chatbot-faqs",
                  "/unanswered-questions",
                ])}`}>
                  <Link to="#" uk-tooltip="Admin" aria-label="Admin">
                    <span className="nav-admin-gear-icon">
                      <FeatherIcon name="settings" />
                    </span>
                  </Link>
                  <NavbarDropdown id="admin-dropdown">
                    <NavItem>
                      <Link to="/settings">Company Settings</Link>
                    </NavItem>
                    <li className="uk-nav-divider" />
                    <NavItem>
                      <Link to="/units">Units</Link>
                    </NavItem>
                    <NavItem>
                      <Link to="/macros">Macros</Link>
                    </NavItem>
                    {hasFlag("crm") && (
                      <NavItem>
                        <Link to="/custom-fields">Custom Fields</Link>
                      </NavItem>
                    )}
                    <li className="uk-nav-divider" />
                    <NavItem>
                      <Link to="/chatbot-faqs">AI Chatbot FAQs</Link>
                    </NavItem>
                    <NavItem>
                      <Link to="/unanswered-questions">AI Chatbot Unanswered Questions</Link>
                    </NavItem>
                    {!+account?.user?.owner_id && (
                      <>
                        <li className="uk-nav-divider" />
                        <NavItem>
                          <Link to="/team">Users</Link>
                        </NavItem>
                        <NavItem>
                          <Link to="/zones">Zones</Link>
                        </NavItem>
                        <li className="uk-nav-divider" />
                      </>
                    )}
                    {!+account?.user?.owner_id && (
                      <NavItem>
                        <Link to="/billing">Billing</Link>
                      </NavItem>
                    )}
                    {!+account?.user?.owner_id && hasExtras() && (
                      <NavItem>
                        <Link to="/payments">Payments</Link>
                      </NavItem>
                    )}
                  </NavbarDropdown>
                </NavItem>
                <NavItem className={`nav-help-item ${isActive(["/whats-new", "/features"])}`}>
                  <Link to="#" uk-tooltip="Help" aria-label="Help">
                    <span className="nav-help-icon">
                      <FeatherIcon name="help-circle" />
                    </span>
                  </Link>
                  <NavbarDropdown id="help-dropdown">
                    <NavItem>
                      <Link to="/help" onClick={() => UIkit.dropdown("#help-dropdown").hide(0)}>
                        Help Center
                      </Link>
                    </NavItem>
                    <NavItem>
                      <Link to="/features" onClick={() => UIkit.dropdown("#help-dropdown").hide(0)}>
                        Your Features
                      </Link>
                    </NavItem>
                    <NavItem>
                      <Link
                        onClick={() => {
                          UIkit.dropdown("#help-dropdown").hide(0);
                        }}
                        to="/whats-new"
                      >
                        What's New
                      </Link>
                    </NavItem>
                    <NavItem>
                      <a
                        href="https://intercom.help/ruebarueapp/en/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Knowledge Base
                      </a>
                    </NavItem>
                  </NavbarDropdown>
                </NavItem>
                <NavItem className={`nav-profile-item ${isActive(["/profile"])}`}>
                  <Link to="#">
                    <img
                      src={profileImage?.url || "/images/profile.svg"}
                      className="nav-profile-avatar"
                      alt="Profile"
                    />
                  </Link>
                  <NavbarDropdown id="profile-dropdown">
                    <NavItem>
                      <Link
                        onClick={() => {
                          UIkit.dropdown("#profile-dropdown").hide(0);
                        }}
                        to="/profile"
                      >
                        Profile
                      </Link>
                    </NavItem>
                    <NavItem>
                      <Link to="#" onClick={handleLogout}>
                        Sign Out
                      </Link>
                    </NavItem>
                  </NavbarDropdown>
                  </NavItem>
                </Navbar>
              )}
            </div>
          </div>
        </NavbarContainer>
      </div>
      <Notification />
    </>
  );
};

export default Header;
